<script setup>
import ClientOnlyTeleport from '@/Components/ClientOnlyTeleport.vue';
import CustomDropDown from '@/Components/CustomDropDown.vue';
import DownloadButton from '@/Components/DownloadButton.vue';
import { useCoachMark } from '@/composables/useCoachMark';
import { useZIndexStore } from '@/stores/zIndexStore';
import { downloadAsset } from '@/utils/assetUrl';
import Icon from '@/Components/Icon.vue';
import { useI18n } from 'vue-i18n';
import { useLocaleFormatting } from '@/composables/useLocaleFormatting';
import axios from 'axios';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue';

const props = defineProps({
    data: Object,
    sheet: Object,
    tab: Object,
    active: {
        type: Boolean,
        default: true,
    },
});

const RT_MEASUREMENTS_TOUR_STORAGE_KEY = 'pompei.rtMeasurements.tourDismissed';
const TOUR_STEP_SELECTORS = {
    dataset: '[data-rt-tour="dataset"]',
    modes: '[data-rt-tour="modes"]',
    drawType: '[data-rt-tour="draw-type"]',
    map: '[data-rt-tour="map"]',
    drawStatus: '[data-rt-tour="draw-status"]',
    results: '[data-rt-tour="results"]',
    actions: '[data-rt-tour="actions"]',
};

const INTERACTION_MODES = {
    view: 'view',
    draw: 'draw',
    edit: 'edit',
};

const DRAW_TYPES = {
    line: 'line',
    area: 'area',
};

// OpenLayers is loaded lazily because this viewer is Pompei-specific and should not weigh down generic sheet pages.
let olModulesPromise = null;
let map = null;
let imageLayer = null;
let vectorLayer = null;
let vectorSource = null;
let drawInteraction = null;
let modifyInteraction = null;
let imageLoadListenerKeys = [];
let drawListenerKeys = [];
let modifyListenerKeys = [];
let mapPointerListenerKeys = [];
let mapPointerLeaveTarget = null;
let mapPointerLeaveHandler = null;
let sketchGeometryListenerKey = null;
let unByKeyFn = null;
let resizeObserver = null;
let mapRefreshFrame = null;
let pendingMapFit = false;
let viewportOverflowSnapshot = null;

const zIndexStore = useZIndexStore();
const { t } = useI18n({ useScope: 'global' });
const { formatNumber } = useLocaleFormatting();

const tabEl = shallowRef(null);
const mapEl = shallowRef(null);
const datasets = ref([]);
const selectedDatasetId = ref('');
const loading = ref(false);
const error = ref('');
const imageLoading = ref(false);
const imageLoadError = ref('');
const interactionMode = ref(INTERACTION_MODES.view);
const drawType = ref(DRAW_TYPES.line);
const isDrawing = ref(false);
const sketchPointCount = ref(0);
const measurements = ref([]);
const hoveredMeasurementId = ref(null);
const isFullscreen = ref(false);
const viewportModeZIndex = ref(0);

const selectedDataset = computed(() => datasets.value.find((dataset) => dataset.id === selectedDatasetId.value) ?? null);
const hasDatasets = computed(() => datasets.value.length > 0);
const canMeasure = computed(() => Boolean(
    selectedDataset.value?.status === 'ready'
    && selectedDataset.value?.image?.asset_url
    && selectedDataset.value?.world_file?.transform?.extent
));
const canEditMeasurements = computed(() => measurements.value.length > 0);
const interactionModeLabel = computed(() => {
    if (interactionMode.value === INTERACTION_MODES.draw) return t('pompei.measurements.modes.draw');
    if (interactionMode.value === INTERACTION_MODES.edit) return t('pompei.measurements.modes.edit');
    return t('pompei.measurements.modes.view');
});
const drawTypeLabel = computed(() => drawType.value === DRAW_TYPES.area
    ? t('pompei.measurements.types.area')
    : t('pompei.measurements.types.distance'));
const minimumSketchPointCount = computed(() => drawType.value === DRAW_TYPES.area ? 3 : 2);
const canFinishDrawing = computed(() => isDrawing.value && sketchPointCount.value >= minimumSketchPointCount.value);
const isTabActive = computed(() => props.active !== false);
const interactionStatusLabel = computed(() => {
    if (!canMeasure.value) return t('pompei.measurements.status.datasetNotReady');
    if (isDrawing.value && canFinishDrawing.value) return t('pompei.measurements.status.finish');
    if (isDrawing.value) return t('pompei.measurements.status.addPoints');
    if (interactionMode.value === INTERACTION_MODES.draw) return t('pompei.measurements.status.drawReady');
    if (interactionMode.value === INTERACTION_MODES.edit) return t('pompei.measurements.status.editActive');
    return t('pompei.measurements.status.mapReady');
});
const canStartTutorial = computed(() => Boolean(
    isTabActive.value
    && tabEl.value
    && hasDatasets.value
    && !loading.value
    && !error.value
));
const viewportModeStyle = computed(() => isFullscreen.value && viewportModeZIndex.value
    ? { zIndex: viewportModeZIndex.value }
    : null);
const datasetDropdownOptions = computed(() => datasets.value.map((dataset) => ({
    ...dataset,
    name: dataset.label ?? dataset.name ?? dataset.complete_label ?? '',
    complete_label: dataset.label ?? dataset.complete_label ?? dataset.name ?? '',
})));
const selectedDatasetOption = computed(() => datasetDropdownOptions.value
    .find((dataset) => dataset.id === selectedDatasetId.value) ?? null);
const downloadOptions = computed(() => [
    selectedDataset.value?.image?.original_download_url
        ? {
            id: 'tif',
            name: t('pompei.measurements.downloads.tif'),
            download_url: selectedDataset.value.image.original_download_url,
        }
        : null,
    selectedDataset.value?.dxf?.download_url
        ? {
            id: 'dxf',
            name: t('pompei.measurements.downloads.dxf'),
            download_url: selectedDataset.value.dxf.download_url,
        }
        : null,
].filter(Boolean));

const handleDatasetChange = (option) => {
    selectedDatasetId.value = option?.id ?? '';
};

const rtRecordId = computed(() => props.data?.referente_topografico_id
    ?? props.data?.referente_topografico?.id
    ?? props.data?.id
    ?? '');

// The manifest endpoint is scoped to the Pompei RT record and returns already grouped TIF/TFW/DXF datasets.
const endpoint = computed(() => rtRecordId.value ? `/api/pompei/rt/${rtRecordId.value}/rectified-datasets` : '');

const findTourElement = (selector) => tabEl.value?.querySelector(selector) ?? null;

const buildTutorialSteps = () => [
    {
        selector: TOUR_STEP_SELECTORS.dataset,
        popover: {
            title: t('pompei.measurements.tour.dataset.title'),
            description: t('pompei.measurements.tour.dataset.description'),
            side: 'bottom',
            align: 'start',
        },
    },
    {
        selector: TOUR_STEP_SELECTORS.modes,
        popover: {
            title: t('pompei.measurements.tour.interaction.title'),
            description: t('pompei.measurements.tour.interaction.description'),
            side: 'bottom',
            align: 'center',
        },
    },
    {
        selector: TOUR_STEP_SELECTORS.drawType,
        popover: {
            title: t('pompei.measurements.tour.type.title'),
            description: t('pompei.measurements.tour.type.description'),
            side: 'bottom',
            align: 'center',
        },
    },
    {
        selector: TOUR_STEP_SELECTORS.map,
        popover: {
            title: t('pompei.measurements.tour.map.title'),
            description: t('pompei.measurements.tour.map.description'),
            side: 'left',
            align: 'center',
        },
    },
    {
        selector: TOUR_STEP_SELECTORS.drawStatus,
        popover: {
            title: t('pompei.measurements.tour.finish.title'),
            description: t('pompei.measurements.tour.finish.description'),
            side: 'top',
            align: 'center',
        },
    },
    {
        selector: TOUR_STEP_SELECTORS.results,
        popover: {
            title: t('pompei.measurements.tour.results.title'),
            description: t('pompei.measurements.tour.results.description'),
            side: 'left',
            align: 'start',
        },
    },
    {
        selector: TOUR_STEP_SELECTORS.actions,
        popover: {
            title: t('pompei.measurements.tour.actions.title'),
            description: t('pompei.measurements.tour.actions.description'),
            side: 'bottom',
            align: 'end',
        },
    },
]
    .map(({ selector, popover }) => {
        const element = findTourElement(selector);
        return element ? { element, popover } : null;
    })
    .filter(Boolean);

const rtMeasurementsTour = useCoachMark({
    id: 'pompei-rt-measurements',
    enabled: () => canStartTutorial.value,
    target: () => tabEl.value,
    canShow: () => buildTutorialSteps().length >= 3,
    storage: 'local',
    storageKey: RT_MEASUREMENTS_TOUR_STORAGE_KEY,
    delayMs: 420,
    retryDelayMs: 160,
    maxAttempts: 8,
    driverOptions: {
        popoverClass: 'rt-measurements-tour-popover',
        showProgress: true,
        showButtons: ['next', 'previous', 'close'],
        nextBtnText: t('pompei.measurements.tour.next'),
        prevBtnText: t('pompei.measurements.tour.previous'),
        doneBtnText: t('pompei.measurements.tour.done'),
    },
    steps: () => buildTutorialSteps(),
});

const clearTutorialDismissal = () => {
    if (typeof window === 'undefined') return;

    try {
        window.localStorage.removeItem(RT_MEASUREMENTS_TOUR_STORAGE_KEY);
    } catch (_exception) {
        // Local storage can be unavailable in locked/private contexts.
    }
};

const startTutorial = async () => {
    if (!canStartTutorial.value) return;

    clearTutorialDismissal();
    rtMeasurementsTour.destroy({ persist: false });
    await nextTick();
    rtMeasurementsTour.show();
};

const loadOpenLayers = () => {
    if (!olModulesPromise) {
        olModulesPromise = Promise.all([
            import('ol/ol.css'),
            import('ol/Map'),
            import('ol/View'),
            import('ol/layer/Image'),
            import('ol/source/ImageStatic'),
            import('ol/layer/Vector'),
            import('ol/source/Vector'),
            import('ol/interaction/Draw'),
            import('ol/interaction/Modify'),
            import('ol/proj/Projection'),
            import('ol/style/Style'),
            import('ol/style/Stroke'),
            import('ol/style/Fill'),
            import('ol/style/Circle'),
            import('ol/Observable'),
        ]).then(([
            _css,
            MapModule,
            ViewModule,
            ImageLayerModule,
            ImageStaticModule,
            VectorLayerModule,
            VectorSourceModule,
            DrawModule,
            ModifyModule,
            ProjectionModule,
            StyleModule,
            StrokeModule,
            FillModule,
            CircleModule,
            ObservableModule,
        ]) => {
            unByKeyFn = ObservableModule.unByKey;

            return {
                Map: MapModule.default,
                View: ViewModule.default,
                ImageLayer: ImageLayerModule.default,
                ImageStatic: ImageStaticModule.default,
                VectorLayer: VectorLayerModule.default,
                VectorSource: VectorSourceModule.default,
                Draw: DrawModule.default,
                Modify: ModifyModule.default,
                Projection: ProjectionModule.default,
                Style: StyleModule.default,
                Stroke: StrokeModule.default,
                Fill: FillModule.default,
                CircleStyle: CircleModule.default,
            };
        });
    }

    return olModulesPromise;
};

const fetchDatasets = async () => {
    if (!endpoint.value) {
        error.value = '';
        datasets.value = [];
        selectedDatasetId.value = '';
        clearMap();
        return;
    }

    loading.value = true;
    error.value = '';
    datasets.value = [];
    selectedDatasetId.value = '';
    clearMap();

    try {
        const response = await axios.get(endpoint.value);
        datasets.value = response.data?.data ?? [];
        selectedDatasetId.value = datasets.value.find((dataset) => dataset.status === 'ready')?.id
            ?? datasets.value[0]?.id
            ?? '';
    } catch (_exception) {
        error.value = 'pompei.measurements.errors.datasets';
    } finally {
        loading.value = false;
    }
};

const extentArray = (dataset) => {
    const extent = dataset?.world_file?.transform?.extent;
    if (!extent) return null;

    return [extent.min_x, extent.min_y, extent.max_x, extent.max_y];
};

const hasVisibleMapViewport = () => {
    if (!mapEl.value) return false;

    const { width, height } = mapEl.value.getBoundingClientRect();
    return width > 0 && height > 0;
};

const refreshMapViewport = ({ fit = false } = {}) => {
    if (!map || !mapEl.value || !hasVisibleMapViewport()) {
        pendingMapFit = pendingMapFit || fit;
        return;
    }

    if (mapRefreshFrame) {
        window.cancelAnimationFrame(mapRefreshFrame);
    }

    mapRefreshFrame = window.requestAnimationFrame(() => {
        mapRefreshFrame = null;

        if (!map || !mapEl.value || !hasVisibleMapViewport()) {
            return;
        }

        map.updateSize();

        if (fit || pendingMapFit) {
            const extent = extentArray(selectedDataset.value);

            if (extent) {
                map.getView().fit(extent, {
                    size: map.getSize(),
                    padding: [24, 24, 24, 24],
                });
            }

            pendingMapFit = false;
        }

        map.renderSync();
    });
};

const observeMapContainer = (element) => {
    if (!element || typeof ResizeObserver === 'undefined') return;

    if (!resizeObserver) {
        resizeObserver = new ResizeObserver(() => {
            refreshMapViewport({ fit: pendingMapFit });
        });
    }

    resizeObserver.observe(element);
};

const unobserveMapContainer = (element) => {
    if (!resizeObserver || !element) return;
    resizeObserver.unobserve(element);
};

const measurementColor = () => {
    if (typeof window === 'undefined') return '#575757';

    return window.getComputedStyle(tabEl.value ?? document.documentElement)
        .getPropertyValue('--color-btn-misure')
        .trim() || '#575757';
};

const colorWithAlpha = (color, alpha) => {
    const hexMatch = color.match(/^#([\da-f]{3}|[\da-f]{6})$/i);

    if (hexMatch) {
        const hex = hexMatch[1].length === 3
            ? hexMatch[1].split('').map((character) => character + character).join('')
            : hexMatch[1];
        const red = parseInt(hex.slice(0, 2), 16);
        const green = parseInt(hex.slice(2, 4), 16);
        const blue = parseInt(hex.slice(4, 6), 16);

        return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
    }

    const rgbMatch = color.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
    if (rgbMatch) return `rgba(${rgbMatch[1]}, ${rgbMatch[2]}, ${rgbMatch[3]}, ${alpha})`;

    return color;
};

const createMeasurementStyle = async ({ highlighted = false } = {}) => {
    const { Style, Stroke, Fill, CircleStyle } = await loadOpenLayers();
    const color = measurementColor();
    const fillColor = colorWithAlpha(color, highlighted ? 0.24 : 0.12);

    return new Style({
        stroke: new Stroke({ color, width: highlighted ? 5 : 3 }),
        fill: new Fill({ color: fillColor }),
        image: new CircleStyle({
            radius: highlighted ? 7 : 5,
            fill: new Fill({ color }),
            stroke: new Stroke({ color: '#ffffff', width: 2 }),
        }),
        zIndex: highlighted ? 10 : 1,
    });
};

const unlistenKeys = (listenerKeys) => {
    if (unByKeyFn) {
        listenerKeys.forEach((listenerKey) => unByKeyFn(listenerKey));
    }

    return [];
};

const clearSketchGeometryListener = () => {
    if (sketchGeometryListenerKey && unByKeyFn) {
        unByKeyFn(sketchGeometryListenerKey);
    }

    sketchGeometryListenerKey = null;
};

const sketchPointCountFromGeometry = (geometry) => {
    const coordinates = geometry?.getCoordinates?.();

    if (!Array.isArray(coordinates)) return 0;

    if (drawType.value === DRAW_TYPES.area) {
        return Math.max((coordinates[0]?.length ?? 0) - 2, 0);
    }

    return Math.max(coordinates.length - 1, 0);
};

const updateSketchPointCount = (geometry) => {
    sketchPointCount.value = sketchPointCountFromGeometry(geometry);
};

const resetSketchState = () => {
    clearSketchGeometryListener();
    isDrawing.value = false;
    sketchPointCount.value = 0;
};

const setHoveredMeasurementId = (id) => {
    const nextId = id ?? null;

    if (hoveredMeasurementId.value === nextId) return;

    hoveredMeasurementId.value = nextId;
    vectorLayer?.changed?.();
};

const clearMapHoverHandlers = () => {
    mapPointerListenerKeys = unlistenKeys(mapPointerListenerKeys);

    if (mapPointerLeaveTarget && mapPointerLeaveHandler) {
        mapPointerLeaveTarget.removeEventListener('pointerleave', mapPointerLeaveHandler);
    }

    mapPointerLeaveTarget = null;
    mapPointerLeaveHandler = null;
};

const attachMapHoverHandlers = () => {
    if (!map) return;

    mapPointerListenerKeys = [
        map.on('pointermove', (event) => {
            if (event.dragging || isDrawing.value) return;

            const feature = map.forEachFeatureAtPixel(
                event.pixel,
                (candidateFeature) => candidateFeature,
                { hitTolerance: 6 }
            );

            setHoveredMeasurementId(feature?.get?.('measurementId') ?? null);
        }),
    ];

    mapPointerLeaveTarget = map.getViewport?.() ?? map.getTargetElement?.() ?? null;
    mapPointerLeaveHandler = () => setHoveredMeasurementId(null);
    mapPointerLeaveTarget?.addEventListener?.('pointerleave', mapPointerLeaveHandler);
};

const measurementTypeLabel = (type) => type === DRAW_TYPES.area
    ? t('pompei.measurements.types.area')
    : t('pompei.measurements.types.distance');

const measurementValue = (geometry, type) => {
    if (!geometry) return 0;

    return type === DRAW_TYPES.area
        ? polygonArea(geometry.getCoordinates()?.[0] ?? [])
        : lineLength(geometry.getCoordinates() ?? []);
};

const buildMeasurementRecord = (id, type, geometry) => {
    const value = measurementValue(geometry, type);

    return {
        id,
        type,
        value,
        label: type === DRAW_TYPES.area ? formatArea(value) : formatDistance(value),
    };
};

const registerMeasurement = (feature, type) => {
    const geometry = feature?.getGeometry?.();
    if (!geometry) return;

    const id = `${Date.now()}-${measurements.value.length}`;
    feature.set('measurementId', id);
    feature.set('measurementType', type);
    measurements.value.push(buildMeasurementRecord(id, type, geometry));
};

const updateMeasurementFromFeature = (feature) => {
    const id = feature?.get?.('measurementId');
    const type = feature?.get?.('measurementType');
    const geometry = feature?.getGeometry?.();

    if (!id || !type || !geometry) return;

    const index = measurements.value.findIndex((measurement) => measurement.id === id);
    if (index === -1) return;

    const nextMeasurements = [...measurements.value];
    nextMeasurements[index] = buildMeasurementRecord(id, type, geometry);
    measurements.value = nextMeasurements;
};

const updateModifyInteraction = () => {
    if (!modifyInteraction) return;
    modifyInteraction.setActive(interactionMode.value === INTERACTION_MODES.edit && canEditMeasurements.value);
};

const clearDrawInteraction = ({ abort = true } = {}) => {
    if (abort && isDrawing.value && drawInteraction?.abortDrawing) {
        drawInteraction.abortDrawing();
    }

    resetSketchState();
    drawListenerKeys = unlistenKeys(drawListenerKeys);

    if (drawInteraction && map) {
        map.removeInteraction(drawInteraction);
    }

    drawInteraction = null;
};

const updateDrawInteraction = async () => {
    clearDrawInteraction();

    if (!map || !vectorSource || interactionMode.value !== INTERACTION_MODES.draw || !canMeasure.value) return;

    const activeDrawType = drawType.value;
    const { Draw } = await loadOpenLayers();

    if (!map || !vectorSource || interactionMode.value !== INTERACTION_MODES.draw || activeDrawType !== drawType.value) {
        return;
    }

    const measurementStyle = await createMeasurementStyle();

    if (!map || !vectorSource || interactionMode.value !== INTERACTION_MODES.draw || activeDrawType !== drawType.value) {
        return;
    }

    drawInteraction = new Draw({
        source: vectorSource,
        type: activeDrawType === DRAW_TYPES.area ? 'Polygon' : 'LineString',
        style: measurementStyle,
    });

    drawListenerKeys = [
        drawInteraction.on('drawstart', (event) => {
            const geometry = event.feature.getGeometry();

            isDrawing.value = true;
            updateSketchPointCount(geometry);
            clearSketchGeometryListener();
            sketchGeometryListenerKey = geometry.on('change', () => updateSketchPointCount(geometry));
        }),
        drawInteraction.on('drawend', (event) => {
            registerMeasurement(event.feature, activeDrawType);
            resetSketchState();
        }),
        drawInteraction.on('drawabort', () => {
            resetSketchState();
        }),
    ];

    map.addInteraction(drawInteraction);
};

const setInteractionMode = (mode) => {
    if (mode === INTERACTION_MODES.draw && !canMeasure.value) return;
    if (mode === INTERACTION_MODES.edit && !canEditMeasurements.value) return;

    interactionMode.value = interactionMode.value === mode ? INTERACTION_MODES.view : mode;
};

const setDrawType = (type) => {
    if (!Object.values(DRAW_TYPES).includes(type)) return;
    drawType.value = type;

    if (interactionMode.value !== INTERACTION_MODES.draw && canMeasure.value) {
        interactionMode.value = INTERACTION_MODES.draw;
    }
};

const finishDrawing = () => {
    if (!drawInteraction || !canFinishDrawing.value) return;
    drawInteraction.finishDrawing();
};

const cancelDrawing = () => {
    if (drawInteraction?.abortDrawing && isDrawing.value) {
        drawInteraction.abortDrawing();
        return;
    }

    resetSketchState();
};

const initMap = async () => {
    await nextTick();

    const dataset = selectedDataset.value;
    const extent = extentArray(dataset);
    if (!mapEl.value || !dataset?.image?.asset_url || !extent) {
        clearMap();
        return;
    }

    clearMap();

    // The viewer works in the native/world-file coordinate space instead of reusing generic image-preview tooling.
    const { Map, View, ImageLayer, ImageStatic, VectorLayer, VectorSource, Modify, Projection } = await loadOpenLayers();
    const projection = new Projection({
        code: dataset.crs?.code ?? `RT:${dataset.id}`,
        units: dataset.crs?.units === 'meters' ? 'm' : 'pixels',
        extent,
    });

    imageLoading.value = true;
    imageLoadError.value = '';

    const imageSource = new ImageStatic({
        url: dataset.image.asset_url,
        projection,
        imageExtent: extent,
    });

    imageLoadListenerKeys = [
        imageSource.on('imageloadstart', () => {
            imageLoading.value = true;
            imageLoadError.value = '';
        }),
        imageSource.on('imageloadend', () => {
            imageLoading.value = false;
        }),
        imageSource.on('imageloaderror', () => {
            imageLoading.value = false;
            imageLoadError.value = 'pompei.measurements.errors.image';
        }),
    ];

    imageLayer = new ImageLayer({
        source: imageSource,
    });

    vectorSource = new VectorSource();
    const measurementStyle = await createMeasurementStyle();
    const highlightedMeasurementStyle = await createMeasurementStyle({ highlighted: true });
    vectorLayer = new VectorLayer({
        source: vectorSource,
        style: (feature) => feature.get('measurementId') === hoveredMeasurementId.value
            ? highlightedMeasurementStyle
            : measurementStyle,
    });

    map = new Map({
        target: mapEl.value,
        layers: [imageLayer, vectorLayer],
        view: new View({
            projection,
            center: [(extent[0] + extent[2]) / 2, (extent[1] + extent[3]) / 2],
            zoom: 2,
            extent,
            showFullExtent: true,
        }),
        controls: [],
    });

    pendingMapFit = true;
    refreshMapViewport({ fit: true });

    map.once('rendercomplete', () => {
        if (!imageLoadError.value) imageLoading.value = false;
        refreshMapViewport();
    });

    modifyInteraction = new Modify({ source: vectorSource });
    modifyInteraction.setActive(false);
    modifyListenerKeys = [
        modifyInteraction.on('modifyend', (event) => {
            event.features?.forEach?.((feature) => updateMeasurementFromFeature(feature));
        }),
    ];
    map.addInteraction(modifyInteraction);
    attachMapHoverHandlers();
    updateModifyInteraction();
    updateDrawInteraction();
};

const clearMap = () => {
    imageLoadListenerKeys = unlistenKeys(imageLoadListenerKeys);

    if (mapRefreshFrame) {
        window.cancelAnimationFrame(mapRefreshFrame);
        mapRefreshFrame = null;
    }

    clearDrawInteraction();
    clearMapHoverHandlers();

    modifyListenerKeys = unlistenKeys(modifyListenerKeys);
    if (modifyInteraction && map) map.removeInteraction(modifyInteraction);
    modifyInteraction = null;
    imageLayer = null;
    vectorLayer = null;
    vectorSource = null;
    measurements.value = [];
    hoveredMeasurementId.value = null;
    interactionMode.value = INTERACTION_MODES.view;
    isDrawing.value = false;
    sketchPointCount.value = 0;
    imageLoading.value = false;
    imageLoadError.value = '';
    pendingMapFit = false;

    if (map) {
        map.setTarget(undefined);
        map = null;
    }
};

const clearMeasurements = () => {
    cancelDrawing();
    vectorSource?.clear();
    measurements.value = [];
    setHoveredMeasurementId(null);

    if (interactionMode.value === INTERACTION_MODES.edit) {
        interactionMode.value = INTERACTION_MODES.view;
    }

    updateModifyInteraction();
};

const removeMeasurement = (id) => {
    const feature = vectorSource?.getFeatures?.().find((candidateFeature) => candidateFeature.get('measurementId') === id);

    if (feature) {
        vectorSource.removeFeature(feature);
    }

    measurements.value = measurements.value.filter((measurement) => measurement.id !== id);

    if (hoveredMeasurementId.value === id) {
        setHoveredMeasurementId(null);
    }

    if (!measurements.value.length && interactionMode.value === INTERACTION_MODES.edit) {
        interactionMode.value = INTERACTION_MODES.view;
    }

    updateModifyInteraction();
};

const measurementOrdinal = (measurement, index) => measurements.value
    .slice(0, index + 1)
    .filter((candidateMeasurement) => candidateMeasurement.type === measurement.type)
    .length;

const handleDatasetDownload = async (url) => {
    if (!url) return;
    await downloadAsset(url);
};

const setViewportOverflowLock = (locked) => {
    if (typeof document === 'undefined' || !document.body) return;

    if (locked) {
        if (!viewportOverflowSnapshot) {
            viewportOverflowSnapshot = {
                documentElement: document.documentElement.style.overflow,
                body: document.body.style.overflow,
            };
        }

        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
        return;
    }

    if (!viewportOverflowSnapshot) return;

    document.documentElement.style.overflow = viewportOverflowSnapshot.documentElement;
    document.body.style.overflow = viewportOverflowSnapshot.body;
    viewportOverflowSnapshot = null;
};

const claimViewportModeZIndex = () => {
    viewportModeZIndex.value = Math.max(zIndexStore.nextZ() + 1000, 2100);
};

const syncViewportMode = () => {
    setViewportOverflowLock(isFullscreen.value);

    nextTick(() => {
        refreshMapViewport({ fit: true });
    });
};

const toggleViewportMode = () => {
    isFullscreen.value = !isFullscreen.value;
};

const lineLength = (coordinates) => coordinates.reduce((total, coordinate, index) => {
    if (index === 0) return total;
    const previous = coordinates[index - 1];
    return total + Math.hypot(coordinate[0] - previous[0], coordinate[1] - previous[1]);
}, 0);

const polygonArea = (coordinates) => {
    if (coordinates.length < 3) return 0;

    const sum = coordinates.reduce((total, coordinate, index) => {
        const next = coordinates[(index + 1) % coordinates.length];
        return total + coordinate[0] * next[1] - next[0] * coordinate[1];
    }, 0);

    return Math.abs(sum / 2);
};

const unitLabel = computed(() => selectedDataset.value?.crs?.units === 'meters'
    ? 'm'
    : t('pompei.measurements.labels.unit'));

const formatDistance = (value) => {
    if (unitLabel.value === 'm' && value >= 1000) {
        return `${formatNumber(value / 1000, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} km`;
    }
    return `${formatNumber(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${unitLabel.value}`;
};

const formatArea = (value) => {
    if (unitLabel.value === 'm' && value >= 10000) {
        return `${formatNumber(value / 10000, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ha`;
    }
    const areaUnit = unitLabel.value === 'm' ? 'm²' : t('pompei.measurements.labels.squareUnits');
    return `${formatNumber(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${areaUnit}`;
};

const isFormControlTarget = (target) => ['BUTTON', 'INPUT', 'SELECT', 'TEXTAREA'].includes(target?.tagName);

const handleMeasurementKeydown = (event) => {
    if (interactionMode.value !== INTERACTION_MODES.draw || !isDrawing.value || isFormControlTarget(event.target)) {
        return;
    }

    if (event.key === 'Enter' && canFinishDrawing.value) {
        event.preventDefault();
        finishDrawing();
    }

    if (event.key === 'Escape') {
        event.preventDefault();
        cancelDrawing();
    }
};

watch(selectedDatasetId, () => {
    interactionMode.value = INTERACTION_MODES.view;
    initMap();
});

watch([interactionMode, drawType], () => {
    updateDrawInteraction();
    updateModifyInteraction();
});

watch(canMeasure, (canUseMeasureTools) => {
    if (!canUseMeasureTools && interactionMode.value !== INTERACTION_MODES.view) {
        interactionMode.value = INTERACTION_MODES.view;
    }
});

watch(canEditMeasurements, (canEdit) => {
    if (!canEdit && interactionMode.value === INTERACTION_MODES.edit) {
        interactionMode.value = INTERACTION_MODES.view;
    }

    updateModifyInteraction();
});

watch(mapEl, (element, previousElement) => {
    unobserveMapContainer(previousElement);
    observeMapContainer(element);

    if (element && map) {
        refreshMapViewport({ fit: pendingMapFit });
    }
});

watch(rtRecordId, () => {
    fetchDatasets();
});

watch(isFullscreen, (expanded) => {
    if (expanded) {
        claimViewportModeZIndex();
    }

    syncViewportMode();
});

watch(isTabActive, async (isActive) => {
    if (!isActive) {
        if (isFullscreen.value) {
            isFullscreen.value = false;
        }

        rtMeasurementsTour.destroy({ persist: false });
        return;
    }

    await nextTick();
    refreshMapViewport({ fit: pendingMapFit });
});

watch(canStartTutorial, (canStart) => {
    if (canStart) {
        rtMeasurementsTour.queue();
    }
}, { immediate: true });

onMounted(() => {
    if (typeof document !== 'undefined') {
        document.addEventListener('keydown', handleMeasurementKeydown);
    }

    fetchDatasets();
});

onBeforeUnmount(() => {
    if (typeof document !== 'undefined') {
        document.removeEventListener('keydown', handleMeasurementKeydown);
    }

    setViewportOverflowLock(false);

    unobserveMapContainer(mapEl.value);

    if (resizeObserver) {
        resizeObserver.disconnect();
        resizeObserver = null;
    }

    clearMap();
});
</script>

<template>
    <ClientOnlyTeleport :disabled="!isFullscreen">
        <section ref="tabEl" class="h-full box-border grid grid-rows-[auto_minmax(0,1fr)] pt-4 gap-5"
            :class="{ 'is-fullscreen fixed inset-0 m-0 w-screen h-dvh min-h-dvh p-[18px] bg-white overflow-hidden overscroll-contain': isFullscreen }"
            :style="viewportModeStyle">
            <div class="rt-measurements-toolbar flex items-center justify-between">
                <div class="flex min-w-0 items-end gap-2" data-rt-tour="dataset">
                    <button type="button"
                        class="flex shrink-0 items-center justify-center rounded-[var(--rounded-btn)] border border-[#5f5f5f] bg-[var(--color-btn-misure)] text-white transition-opacity disabled:cursor-not-allowed disabled:opacity-45 height_btn width_btn"
                        :disabled="!canStartTutorial" :aria-label="t('pompei.measurements.actions.guide')"
                        :title="t('pompei.measurements.actions.guide')" @click="startTutorial">
                        <Icon name="guia" />
                    </button>
                    <div class="flex height_btn min-w-0 flex-col gap-1">
                        <CustomDropDown class="min-w-[150px] h-full max-w-[152px]" :options="datasetDropdownOptions"
                            :selected="selectedDatasetOption" :disabled="loading || !hasDatasets" background="bg-white"
                            colorIcon="#111" :title="t('pompei.measurements.labels.dataset')" principalClass="!border-[#5f5f5f] !px-2 !py-0"
                            classText="text-[12px] font-bold uppercase tracking-[0.18em] text-[var(--color-principal)] pr-5"
                            classTextChildren="text-[10px] font-bold uppercase tracking-[0.18em] text-[var(--color-principal)]"
                            @update:model-value="handleDatasetChange" />
                    </div>
                </div>

                <div class="flex flex-1 items-center justify-end gap-1.5 pr-[8.7rem]" data-rt-tour="draw-type">
                    <button type="button"
                        class="height_btn min-w-[110px] rounded-[var(--rounded-btn)] border border-[#5f5f5f] bg-[var(--color-btn-misure)] px-5 text-[12px] tracking-wider font-bold uppercase text-white transition-[opacity,box-shadow] disabled:cursor-not-allowed disabled:opacity-45"
                        :class="interactionMode === INTERACTION_MODES.draw && drawType === DRAW_TYPES.line ? '!bg-white !text-[var(--color-principal)]' : ''"
                        :disabled="!canMeasure"
                        :aria-pressed="interactionMode === INTERACTION_MODES.draw && drawType === DRAW_TYPES.line"
                        @click="setDrawType(DRAW_TYPES.line)">
                        {{ t('pompei.measurements.types.distance') }}
                    </button>
                    <button type="button"
                        class="height_btn min-w-[110px] rounded-[var(--rounded-btn)] border border-[#5f5f5f] bg-[var(--color-btn-misure)] px-5 text-[12px] tracking-wider font-bold uppercase text-white transition-[opacity,box-shadow] disabled:cursor-not-allowed disabled:opacity-45"
                        :class="interactionMode === INTERACTION_MODES.draw && drawType === DRAW_TYPES.area ? '!bg-white !text-[var(--color-principal)]' : ''"
                        :disabled="!canMeasure"
                        :aria-pressed="interactionMode === INTERACTION_MODES.draw && drawType === DRAW_TYPES.area"
                        @click="setDrawType(DRAW_TYPES.area)">
                        {{ t('pompei.measurements.types.area') }}
                    </button>
                </div>

                <div class="flex shrink-0 items-center justify-end gap-1.5" data-rt-tour="actions">
                    <button type="button"
                        class="flex items-center justify-center rounded-[var(--rounded-btn)] border border-[#5f5f5f] bg-[var(--color-btn-misure)] text-white transition-opacity disabled:cursor-not-allowed disabled:opacity-45 height_btn width_btn"
                        :class="{ 'ring-1 ring-[#111]': interactionMode === INTERACTION_MODES.edit }"
                        :disabled="!canEditMeasurements" :aria-label="t('pompei.measurements.actions.edit')"
                        :title="t('pompei.measurements.actions.edit')"
                        :aria-pressed="interactionMode === INTERACTION_MODES.edit"
                        @click="setInteractionMode(INTERACTION_MODES.edit)">
                        <Icon name="pencilWhite" />
                    </button>
                    <!-- <button type="button"
                        class="flex items-center text-white justify-center rounded-[var(--rounded-btn)] border border-[#5f5f5f] bg-[var(--color-btn-misure)] transition-opacity disabled:cursor-not-allowed disabled:opacity-45 height_btn width_btn"
                        :disabled="!measurements.length" aria-label="Cancella" title="Cancella"
                        @click="clearMeasurements">
                        <Icon name="trash" />
                    </button> -->
                    <div class="flex items-end text-white gap-2 bg-[var(--color-btn-misure)] rounded-[var(--rounded-btn)]"
                        data-rt-tour="results">
                        <DownloadButton background="transparent" :disabled="!downloadOptions.length"
                            :options="downloadOptions" class="rt-measurements-download-control "
                            @isDownload="handleDatasetDownload" />
                    </div>
                    <button type="button"
                        class="rt-measurements-fullscreen-button flex items-center justify-center rounded-[var(--rounded-btn)] border border-[#5f5f5f] bg-[var(--color-btn-misure)] text-white transition-opacity disabled:cursor-not-allowed disabled:opacity-45 height_btn width_btn"
                        :class="{ active: isFullscreen }"
                        :aria-label="isFullscreen ? t('pompei.measurements.actions.restoreSize') : t('pompei.measurements.actions.expand')"
                        :title="isFullscreen ? t('pompei.measurements.actions.restoreSize') : t('pompei.measurements.actions.expand')"
                        @click="toggleViewportMode">
                        <Icon name="wb-max" />
                    </button>
                </div>
            </div>

            <div class="grid min-h-0 grid-rows-[minmax(0,1fr)]">
                <div v-if="loading" role="status" aria-live="polite"
                    class="flex items-center justify-center gap-3 bg-[#f4f8f7] py-[10px] px-3 text-[13px] text-[var(--color-principal)]">
                    <div class="rt-measurements-loader h-4 w-4 rounded-full border-2 border-[rgba(0,131,111,0.18)] border-t-[var(--color-principal)]"
                        aria-hidden="true" />
                    <span class="font-normal">{{ t('pompei.measurements.loading.data') }}</span>
                </div>
                <div v-else-if="error" class=" bg-[#fff5f3] py-[10px] px-3 text-[13px] text-[var(--color-principal)]">
                    {{ t(error) }}</div>
                <div v-else-if="!hasDatasets"
                    class=" bg-[#f4f8f7] py-[10px] px-3 text-[13px] text-[var(--color-principal)]">
                    {{ t('pompei.measurements.empty.datasets') }}
                </div>
                <template v-else>
                    <div v-if="selectedDataset?.warnings?.length"
                        class=" bg-[#fff8ec] py-[10px] px-3 text-[13px] text-[var(--color-principal)] [&_p+p]:mt-1">
                        <p v-for="warning in selectedDataset.warnings" :key="warning">{{ warning }}</p>
                    </div>

                    <div class="rt-measurements-body grid h-full min-h-0 grid-cols-[minmax(0,1fr)_300px] gap-4">
                        <div class="flex min-h-0 flex-col gap-4" data-rt-tour="map">
                            <div class="rt-measurements-map-stage relative min-h-0 flex-1">
                                <div ref="mapEl"
                                    class="rt-measurements-map flex-1 h-full min-h-0 overflow-hidden rounded-[var(--rounded-btn)] border border-[#d6d6d6] bg-[#f3f4f4]"
                                    :class="{
                                        'opacity-65': !canMeasure,
                                        'cursor-crosshair': interactionMode === INTERACTION_MODES.draw,
                                        'cursor-grab': interactionMode === INTERACTION_MODES.edit,
                                    }" />
                                <div v-if="imageLoading || imageLoadError"
                                    class="absolute inset-0 z-[1] grid content-center justify-items-center gap-2.5 rounded-[var(--rounded-btn)] p-5 text-center"
                                    :class="imageLoadError ? 'bg-[rgba(255,245,243,0.92)] text-[#9b2d20]' : 'bg-[rgba(243,244,244,0.86)] text-[var(--color-principal)]'"
                                    :role="imageLoadError ? 'alert' : 'status'" aria-live="polite">
                                    <div v-if="imageLoading"
                                        class="rt-measurements-loader h-[42px] w-[42px] rounded-full border-[3px] border-[rgba(0,131,111,0.18)] border-t-[var(--color-principal)]"
                                        aria-hidden="true" />
                                    <p class="m-0 text-[13px] font-normal">{{ imageLoadError
                                        ? t(imageLoadError)
                                        : t('pompei.measurements.loading.image') }}</p>
                                </div>
                            </div>

                            <div class="flex justify-end gap-1.5" data-rt-tour="draw-status" aria-live="polite">
                                <p class="sr-only">{{ interactionStatusLabel }}</p>
                                <div class="flex justify-end gap-1.5">
                                    <button type="button"
                                        class="height_btn min-w-[84px] rounded-[var(--rounded-btn)] border border-[#5f5f5f] bg-[var(--color-btn-misure)] px-3 text-[12px] font-bold uppercase tracking-[0.08em] text-white transition-opacity disabled:cursor-not-allowed disabled:opacity-45"
                                        :disabled="!canFinishDrawing" @click="finishDrawing">
                                        {{ t('pompei.measurements.actions.finish') }}
                                    </button>
                                    <!-- <button type="button"
                                        class="height_btn min-w-[84px] rounded-[var(--rounded-btn)] border border-[#5f5f5f] bg-[var(--color-btn-misure)] px-3 text-[12px] font-bold uppercase tracking-[0.08em] text-white transition-opacity disabled:cursor-not-allowed disabled:opacity-45"
                                        :disabled="!isDrawing" @click="cancelDrawing">
                                        Annulla
                                    </button> -->
                                </div>
                            </div>
                        </div>

                        <aside
                            class="rt-measurements-results grid max-h-full content-start gap-3 overflow-y-auto border-l border-[#d6d6d6] pl-3.5 text-[12px] text-[var(--color-principal)]"
                            data-rt-tour="results">
                            <div>
                                <p
                                    class="mb-0.5 text-[12px] font-bold uppercase leading-[1.2] tracking-[0.06em] text-[var(--color-principal)]">
                                    {{ t('pompei.measurements.labels.interaction') }}</p>
                                <p class="font-normal uppercase leading-tight text-[var(--color-principal)]">{{
                                    interactionModeLabel }}
                                </p>
                            </div>
                            <div>
                                <p
                                    class="mb-0.5 text-[12px] font-bold uppercase leading-[1.2] tracking-[0.06em] text-[var(--color-principal)]">
                                    {{ t('pompei.measurements.labels.measurementType') }}</p>
                                <p class="font-normal uppercase leading-tight text-[var(--color-principal)]">{{
                                    drawTypeLabel }}</p>
                            </div>
                            <div>
                                <p
                                    class="mb-0.5 text-[12px] font-bold uppercase leading-[1.2] tracking-[0.06em] text-[var(--color-principal)]">
                                    {{ t('pompei.measurements.labels.crs') }}</p>
                                <p class="font-normal uppercase leading-tight text-[var(--color-principal)]">{{
                                    selectedDataset?.crs?.code
                                    ?? t('pompei.measurements.labels.native') }}</p>
                            </div>
                            <div>
                                <p
                                    class="mb-0.5 text-[12px] font-bold uppercase leading-[1.2] tracking-[0.06em] text-[var(--color-principal)]">
                                    {{ t('pompei.measurements.labels.resolution') }}</p>
                                <p class="font-normal uppercase leading-tight text-[var(--color-principal)]">
                                    <span v-if="selectedDataset?.world_file?.transform?.pixel_size">
                                        {{ formatNumber(selectedDataset.world_file.transform.pixel_size.x, { minimumFractionDigits: 4, maximumFractionDigits: 4 }) }} x
                                        {{ formatNumber(selectedDataset.world_file.transform.pixel_size.y, { minimumFractionDigits: 4, maximumFractionDigits: 4 }) }} {{ unitLabel
                                        }} /
                                        px
                                    </span>
                                    <span v-else>{{ t('pompei.measurements.labels.unavailable') }}</span>
                                </p>
                            </div>
                            <div class="border-t border-[#d6d6d6] pt-3">
                                <ol v-if="measurements.length" class="grid gap-1">
                                    <li v-for="(measurement, index) in measurements" :key="measurement.id"
                                        class="flex items-center rounded-[var(--rounded-btn)] bg-[#f0f0f0] h-8.5 pl-2.5 pr-1.5 transition-colors"
                                        :class="{ '!bg-[#e4e4e4]': hoveredMeasurementId === measurement.id }"
                                        @mouseenter="setHoveredMeasurementId(measurement.id)"
                                        @mouseleave="setHoveredMeasurementId(null)">
                                        <span
                                            class="min-w-0 truncate text-[12px] font-medium uppercase tracking-[0.05em] text-[var(--color-principal)]">
                                            {{ measurementTypeLabel(measurement.type) }} {{
                                                measurementOrdinal(measurement, index) }}
                                        </span>
                                        <strong
                                            class="ml-auto whitespace-nowrap px-2 text-[12px] font-normal uppercase text-[var(--color-principal)]">
                                            {{ measurement.label }}
                                        </strong>
                                        <button type="button" class="!text-[var(--color-principal)] ml-2"
                                            :aria-label="t('pompei.measurements.actions.deleteMeasurement', { type: measurementTypeLabel(measurement.type), number: measurementOrdinal(measurement, index) })"
                                            @click="removeMeasurement(measurement.id)">
                                            <Icon name="trash" class="w-4 h-4" />
                                        </button>
                                    </li>
                                </ol>
                                <p v-else class="text-[12px] font-medium uppercase text-[var(--color-principal)]">--</p>
                            </div>
                        </aside>
                    </div>
                </template>
            </div>
        </section>
    </ClientOnlyTeleport>
</template>

<style scoped>
.rt-measurements-loader {
    animation: rt-measurements-spin 0.9s linear infinite;
}

@keyframes rt-measurements-spin {
    to {
        transform: rotate(360deg);
    }
}

:global(.rt-measurements-tour-popover) {
    max-width: 340px;
}

:global(.rt-measurements-tour-popover .driver-popover-description) {
    line-height: 1.45;
}
</style>
